import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useUI } from '../../../components/common/UIMessageProvider';
import {
  AttendanceRecord,
  LibraryMember,
  getAttendanceRecords,
  getLibraryMembers,
} from '../../../firebase/libraryFirestoreService';

type TopAttendeeRow = {
  memberId: string;
  fullName: string;
  totalVisits: number;
  lastVisit?: Date;
  status?: string;
};

const ITEMS_PER_PAGE = 20;

const TopAttendees: React.FC = () => {
  const auth = useAuth();
  const ui = useUI();

  const canView =
    auth.canPerformAction('library', 'view') ||
    auth.canPerformAction('attendance', 'view');

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<TopAttendeeRow[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const formatDate = (date?: Date) => {
    if (!date) return 'N/A';
    return (
      date.toLocaleDateString() +
      ' ' +
      date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    );
  };

  const formatFullDate = (value: string) => {
    const [yearText, monthText, dayText] = value.split('-');
    const year = Number(yearText);
    const month = Number(monthText);
    const day = Number(dayText);

    if (!year || !month || !day) return value;

    const parsed = new Date(year, month - 1, day);
    if (Number.isNaN(parsed.getTime())) return value;

    const suffix =
      day % 10 === 1 && day !== 11
        ? 'st'
        : day % 10 === 2 && day !== 12
        ? 'nd'
        : day % 10 === 3 && day !== 13
        ? 'rd'
        : 'th';

    return `${day}${suffix} ${parsed.toLocaleString('en-US', {
      month: 'long',
    })} ${parsed.getFullYear()}`;
  };

  const buildRows = (attendance: AttendanceRecord[], members: LibraryMember[]) => {
    const map = new Map<string, TopAttendeeRow>();

    attendance.forEach((record) => {
      const memberId = String(record.memberId || '').trim();
      const recordName = String(record.fullName || '').trim() || 'Unknown';
      const recordTime =
        record.timeRecorded instanceof Date
          ? record.timeRecorded
          : record.timeRecorded
          ? new Date(record.timeRecorded)
          : undefined;

      let rowKey = '';
      if (memberId) rowKey = `id:${memberId}`;
      else if (recordName && recordName !== 'Unknown')
        rowKey = `name:${recordName.toLowerCase()}`;
      else rowKey = `rec:${record.id || Math.random().toString(36).slice(2)}`;

      const existing = map.get(rowKey);
      if (!existing) {
        map.set(rowKey, {
          memberId: memberId || 'N/A',
          fullName: recordName,
          totalVisits: 1,
          lastVisit: recordTime,
        });
        return;
      }

      existing.totalVisits += 1;
      if (recordTime && (!existing.lastVisit || recordTime > existing.lastVisit)) {
        existing.lastVisit = recordTime;
      }
      if ((!existing.fullName || existing.fullName === 'Unknown') && recordName) {
        existing.fullName = recordName;
      }
    });

    const memberMap = new Map<string, LibraryMember>();
    members.forEach((member) => {
      if (member.memId) memberMap.set(member.memId, member);
    });

    const merged = Array.from(map.values()).map((row) => {
      let member =
        row.memberId !== 'N/A' ? memberMap.get(row.memberId) : undefined;

      if (!member && row.fullName && row.fullName !== 'Unknown') {
        const nameLower = row.fullName.toLowerCase();
        member = members.find(
          (m) => (m.fullName || '').toLowerCase() === nameLower
        ) as LibraryMember | undefined;
      }

      return {
        ...row,
        memberId: member?.memId || row.memberId,
        fullName: member?.fullName || row.fullName,
        status: member?.status || row.status || 'Unknown',
      } as TopAttendeeRow;
    });

    merged.sort((a, b) => {
      const diff = (b.totalVisits || 0) - (a.totalVisits || 0);
      if (diff !== 0) return diff;

      const aTime = a.lastVisit ? new Date(a.lastVisit).getTime() : 0;
      const bTime = b.lastVisit ? new Date(b.lastVisit).getTime() : 0;
      const timeDiff = bTime - aTime;
      if (timeDiff !== 0) return timeDiff;

      return (a.fullName || '').localeCompare(b.fullName || '');
    });

    return merged;
  };

  const fetchTopAttendees = async () => {
    if (!canView) return;

    setLoading(true);
    try {
      const start = startDate ? new Date(startDate) : undefined;
      if (start) start.setHours(0, 0, 0, 0);
      const end = endDate ? new Date(endDate) : undefined;
      if (end) end.setHours(23, 59, 59, 999);

      const [attendanceResult, membersResult] = await Promise.all([
        getAttendanceRecords(
          start && end ? { startDate: start, endDate: end } : undefined
        ),
        getLibraryMembers(),
      ]);

      const attendance = attendanceResult.success ? attendanceResult.data : [];
      const members = membersResult.success ? membersResult.data : [];

      const built = buildRows(attendance, members);
      setRows(built);
      setCurrentPage(1);
    } catch (error: any) {
      const msg = error instanceof Error ? error.message : String(error);
      ui.showMessage(`Error loading top attendees: ${msg}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };
    // Print handler - same report UI style as AttendanceManagement
  const handlePrint = useCallback(() => {
    if (!rows.length) {
      ui.showMessage('Nothing to print - no attendees found.', { type: 'info' });
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      ui.showMessage('Unable to open print window. Please allow pop-ups.', {
        type: 'error',
      });
      return;
    }

    let dateRangeText = 'All dates';
    if (startDate && endDate) {
      dateRangeText = `${formatFullDate(startDate)} - ${formatFullDate(endDate)}`;
    } else if (startDate) {
      dateRangeText = `From: ${formatFullDate(startDate)}`;
    } else if (endDate) {
      dateRangeText = `To: ${formatFullDate(endDate)}`;
    }

    const totalVisits = rows.reduce((sum, row) => sum + (row.totalVisits || 0), 0);
    const topVisits = rows.length > 0 ? rows[0].totalVisits || 0 : 0;
    const activeMembers = rows.filter(
      row => (row.status || '').toLowerCase() === 'active'
    ).length;

    const tableRows = rows
      .map(
        (row, idx) => `
          <tr>
            <td><strong>${idx + 1}</strong></td>
            <td style="font-family: monospace; font-weight: 500;">${row.memberId}</td>
            <td style="font-weight: 600; color: #111827;">${row.fullName}</td>
            <td>${row.status || 'Unknown'}</td>
            <td>${row.totalVisits}</td>
            <td>${formatDate(row.lastVisit)}</td>
          </tr>
        `
      )
      .join('');

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Library Top Attendees Report</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }

          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            padding: 20px;
            background: linear-gradient(135deg, #236779 0%, #0e7490 100%);
            min-height: 100vh;
          }

          .container {
            background: white;
            border-radius: 16px;
            box-shadow: 0 20px 60px rgba(0,0,0,0.3);
            overflow: hidden;
          }

          .header {
            background: linear-gradient(135deg, #236779 0%, #0e7490 100%);
            color: white;
            padding: 30px;
            text-align: center;
            position: relative;
            overflow: hidden;
          }

          .header::before {
            content: '';
            position: absolute;
            top: -50%;
            right: -50%;
            width: 200%;
            height: 200%;
            background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
            animation: pulse 4s ease-in-out infinite;
          }

          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 0.5; }
            50% { transform: scale(1.1); opacity: 0.8; }
          }

          .header-content {
            position: relative;
            z-index: 1;
          }

          .foundation-name {
            font-size: 18px;
            font-weight: 600;
            margin-bottom: 12px;
            letter-spacing: 1px;
            opacity: 0.95;
          }

          .header h1 {
            font-size: 32px;
            font-weight: 700;
            margin-bottom: 8px;
            text-transform: uppercase;
            letter-spacing: 2px;
            text-shadow: 2px 2px 4px rgba(0,0,0,0.2);
          }

          .header p {
            font-size: 14px;
            opacity: 0.95;
            font-weight: 300;
          }

          .stats-section {
            padding: 25px;
            background: linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%);
          }

          .stats-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 15px;
            margin-bottom: 20px;
          }

          .stat-card {
            background: white;
            border-radius: 12px;
            padding: 20px;
            text-align: center;
            box-shadow: 0 4px 12px rgba(0,0,0,0.08);
            border-left: 4px solid;
            transition: transform 0.2s;
            position: relative;
            overflow: hidden;
          }

          .stat-card::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 3px;
            background: linear-gradient(90deg, transparent, currentColor, transparent);
            opacity: 0.3;
          }

          .stat-card.teal { border-left-color: #0f766e; color: #0f766e; }
          .stat-card.cyan { border-left-color: #0891b2; color: #0891b2; }
          .stat-card.blue { border-left-color: #0369a1; color: #0369a1; }
          .stat-card.emerald { border-left-color: #059669; color: #059669; }

          .stat-label {
            font-size: 12px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 1px;
            opacity: 0.8;
            margin-bottom: 10px;
          }

          .stat-value {
            font-size: 32px;
            font-weight: 700;
            line-height: 1;
          }

          .table-container {
            padding: 0 25px 25px;
          }

          table {
            width: 100%;
            border-collapse: separate;
            border-spacing: 0;
            font-size: 11px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.05);
            border-radius: 8px;
            overflow: hidden;
          }

          thead {
            background: linear-gradient(135deg, #236779 0%, #0e7490 100%);
            color: white;
          }

          th {
            padding: 12px 10px;
            text-align: left;
            font-weight: 600;
            text-transform: uppercase;
            font-size: 10px;
            letter-spacing: 0.5px;
            border-bottom: 2px solid rgba(255,255,255,0.2);
          }

          tbody tr {
            background: white;
            transition: background 0.2s;
          }

          tbody tr:nth-child(even) {
            background: #f9fafb;
          }

          tbody tr:hover {
            background: #f3f4f6;
          }

          td {
            padding: 10px;
            border-bottom: 1px solid #e5e7eb;
          }

          tbody tr:last-child td {
            border-bottom: none;
          }

          .footer {
            background: linear-gradient(to right, #f9fafb, #f3f4f6);
            padding: 15px 25px;
            border-top: 2px solid #e5e7eb;
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 11px;
            color: #6b7280;
            flex-wrap: wrap;
            gap: 10px;
          }

          .footer-left {
            display: flex;
            gap: 20px;
            align-items: center;
            flex-wrap: wrap;
          }

          .footer-right {
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: 3px;
          }

          .copyright {
            font-size: 10px;
            color: #9ca3af;
            font-style: italic;
          }

          .footer-item {
            display: flex;
            align-items: center;
            gap: 5px;
          }

          .footer-label {
            font-weight: 600;
            color: #374151;
          }

          @media print {
            body {
              padding: 0;
              background: white;
            }

            .container {
              box-shadow: none;
            }

            .stat-card {
              break-inside: avoid;
            }

            tbody tr {
              break-inside: avoid;
            }
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <div class="header-content">
              <div class="foundation-name">ALHAJI INUSAH FOUNDATION</div>
              <h1>LIBRARY TOP ATTENDEES REPORT</h1>
              <p>Period: ${dateRangeText} | Generated on ${new Date().toLocaleString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}</p>
            </div>
          </div>

          <div class="stats-section">
            <div class="stats-grid">
              <div class="stat-card teal">
                <div class="stat-label">Total Attendees</div>
                <div class="stat-value">${rows.length}</div>
              </div>
              <div class="stat-card cyan">
                <div class="stat-label">Total Visits</div>
                <div class="stat-value">${totalVisits}</div>
              </div>
              <div class="stat-card blue">
                <div class="stat-label">Top Visits</div>
                <div class="stat-value">${topVisits}</div>
              </div>
              <div class="stat-card emerald">
                <div class="stat-label">Active Members</div>
                <div class="stat-value">${activeMembers}</div>
              </div>
            </div>
          </div>

          <div class="table-container">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Member ID</th>
                  <th>Full Name</th>
                  <th>Status</th>
                  <th>Total Visits</th>
                  <th>Last Visit</th>
                </tr>
              </thead>
              <tbody>
                ${tableRows}
              </tbody>
            </table>
          </div>

          <div class="footer">
            <div class="footer-left">
              <div class="footer-item">
                <span class="footer-label">Total Attendees:</span>
                <span>${rows.length}</span>
              </div>
              <div class="footer-item">
                <span class="footer-label">Period:</span>
                <span>${dateRangeText}</span>
              </div>
            </div>
            <div class="footer-right">
              <div>
                <span class="footer-label">Report Date:</span> ${new Date().toLocaleDateString()}
              </div>
              <div class="copyright">
                &copy; ${new Date().getFullYear()} Alhaji Inusah Foundation. All rights reserved.
              </div>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  }, [rows, startDate, endDate, formatDate, ui]);

  useEffect(() => {
    fetchTopAttendees();
  }, [canView]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(rows.length / ITEMS_PER_PAGE));
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [rows.length, currentPage]);

  const totalPages = Math.max(1, Math.ceil(rows.length / ITEMS_PER_PAGE));
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const pagedRows = useMemo(
    () => rows.slice(startIndex, endIndex),
    [rows, startIndex, endIndex]
  );

  if (!canView) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <p className="text-gray-500">
            You don't have permission to view top attendees.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-soft p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-800">
            Top Attendees (by visits)
          </h2>
          <p className="text-xs text-gray-500">
            Members ordered by total visits, then A–Z.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <label className="flex items-center gap-1">
              <span className="hidden sm:inline">From</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="ml-1 px-2 py-1 border rounded text-xs"
              />
            </label>
            <label className="flex items-center gap-1">
              <span className="hidden sm:inline">To</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="ml-1 px-2 py-1 border rounded text-xs"
              />
            </label>
          </div>

          <button
            onClick={fetchTopAttendees}
            className="px-3 py-2 rounded-md bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200"
          >
            Load Data
          </button>

          <button
            onClick={handlePrint}
            className="px-3 py-2 rounded-md bg-white border text-gray-700 text-xs font-medium hover:bg-gray-50"
            title="Print full attendees list"
          >
            Print
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-3 text-sm text-gray-600">
                Loading top attendees...
              </p>
            </div>
          </div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-sm text-gray-600">
            No attendance records found.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">#</th>
                    <th className="px-3 py-2 text-left font-semibold">
                      Member ID
                    </th>
                    <th className="px-3 py-2 text-left font-semibold">
                      Full Name
                    </th>
                    <th className="px-3 py-2 text-left font-semibold">
                      Status
                    </th>
                    <th className="px-3 py-2 text-left font-semibold">
                      Total Visits
                    </th>
                    <th className="px-3 py-2 text-left font-semibold">
                      Last Visit
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pagedRows.map((row, idx) => (
                    <tr
                      key={`${row.memberId}-${startIndex + idx}-${row.fullName.replace(
                        /\s+/g,
                        '_'
                      )}`}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-3 py-2 text-gray-500">
                        {startIndex + idx + 1}
                      </td>
                      <td className="px-3 py-2 font-mono text-gray-700">
                        {row.memberId}
                      </td>
                      <td className="px-3 py-2 font-medium text-gray-800">
                        {row.fullName}
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {row.status || 'Unknown'}
                      </td>
                      <td className="px-3 py-2 text-gray-800">
                        {row.totalVisits}
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {formatDate(row.lastVisit)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3 text-xs text-gray-600">
              <div>
                Showing {rows.length === 0 ? 0 : startIndex + 1}-
                {Math.min(endIndex, rows.length)} of {rows.length}
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="px-2 py-1 rounded border border-gray-200 disabled:opacity-50"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Prev
                </button>
                <span>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  className="px-2 py-1 rounded border border-gray-200 disabled:opacity-50"
                  onClick={() => setCurrentPage((p) =>
                    Math.min(totalPages, p + 1)
                  )}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TopAttendees;


